import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { Button, Chip } from '@nyce/admin-kit/ui';
import { EmptyState, ErrorBox, Page, TableScroller, useInterval } from '../../components/common';
import { IssueList, Kpi, joinParts } from './shared';
import { adminIssues, useAdminSummary } from './admin';

/** Admin dashboard: registry and discovery coverage, and the last 24 h of the audit log. */
export function AdminDashboardPage() {
  const res = useAdminSummary(true);
  useInterval(() => void res.reload(), 60_000, true);
  const s = res.data;
  return (
    <Page
      title="Admin"
      description="Registry, GCP discovery and the audit log of the last 24 hours."
      actions={
        <Button
          size="sm"
          variant="ghost"
          icon={<RefreshCw size={14} />}
          loading={res.loading}
          onClick={() => void res.reload()}
        >
          Refresh
        </Button>
      }
    >
      <ErrorBox error={res.error} />
      {!s && res.loading && <div className="oph-dash-skeleton" aria-busy="true" />}
      {s && (
        <div className={res.loading ? 'oph-dash-stale' : undefined}>
          <div className="nyk-kpis" data-testid="admin-kpis">
            <Kpi
              label="GKE clusters"
              value={s.clusters.total}
              sub={joinParts([
                `${s.clusters.discovered} discovered`,
                s.clusters.disabled > 0 && `${s.clusters.disabled} disabled`,
              ])}
              to="/admin/clusters"
            />
            <Kpi
              label="Environments"
              value={s.environments.registered}
              sub={`registered · ${s.environments.namespaces} more namespaces`}
              to="/admin/environments"
            />
            <Kpi
              label="DB credentials"
              value={s.servers.total}
              sub={joinParts([
                `${s.servers.linked} linked to GCP`,
                s.withoutCredentials > 0 && `${s.withoutCredentials} instances without`,
              ])}
              to="/admin/db-servers"
            />
            <Kpi
              label="Audit · 24 h"
              value={`${s.audit.writes} writes`}
              sub={joinParts([
                s.audit.errors.length > 0 && `${s.audit.errors.length} errors`,
                s.audit.denied.length > 0 && `${s.audit.denied.length} denied`,
              ])}
              tone={s.audit.errors.length ? 'amber' : undefined}
              to="/admin/audit"
            />
            {s.discovery && (
              <Kpi
                label="GCP discovery"
                value={`${s.discovery.projects.length} projects`}
                sub={joinParts([
                  `${s.discovery.instances.length} DB instances`,
                  s.discovery.errors.length > 0 && `${s.discovery.errors.length} errors`,
                ])}
                tone={s.discovery.errors.length ? 'amber' : undefined}
              />
            )}
          </div>
          <IssueList
            issues={adminIssues(s)}
            title="Admin issues"
            clear="No failed or denied writes in the last 24 hours."
          />
          <section className="oph-dash-section" aria-labelledby="dash-audit">
            <div className="oph-dash-section-head">
              <h2 id="dash-audit">Latest changes</h2>
              <Link to="/admin/audit" className="nyk-ui-hint">
                Audit log
              </Link>
            </div>
            {!s.audit.recent.length ? (
              <EmptyState card title="No audit records yet" />
            ) : (
              <div className="nyk-card">
                <TableScroller>
                  <table className="nyk-ui-table nyk-cards" data-testid="recent-audit">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Who</th>
                        <th>What</th>
                        <th>Outcome</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.audit.recent.map((a) => (
                        <tr key={a.id}>
                          <td className="nyk-card-title nyk-nowrap">
                            {new Date(a.occurredAt).toLocaleString()}
                          </td>
                          <td data-label="Who">{a.userName || a.userSub}</td>
                          <td data-label="What" className="nyk-wrap">
                            {a.action}{' '}
                            <span className="nyk-ui-hint">
                              {a.targetType} {a.targetId}
                            </span>
                          </td>
                          <td data-label="Outcome">
                            <Chip size="sm" className={`oph-outcome oph-outcome-${a.outcome}`}>
                              {a.outcome}
                            </Chip>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroller>
              </div>
            )}
          </section>
        </div>
      )}
    </Page>
  );
}
